import {
  type SearchResult,
  useInfiniteScroll,
  useLazyGetSearchResultsQuery,
} from '@carboncopyco/data';
import { DemoIndicatorColoredIcon, InfoIcon } from '@carboncopyco/icons';
import { SearchNoMatchIllustration } from '@carboncopyco/illustrations';
import type { CreatorFilter, IDemo, Sort } from '@carboncopyco/types';
import {
  Box,
  Button,
  ColorHex,
  EmptyView,
  ErrorBox,
  Flex,
  Grid,
  InputText,
  LoadingDots,
  MessageBox,
  Modal,
  ScrollContainer,
  SkeletonImageLoading,
  TextBase,
  TextLargeParagraph,
  TooltipBubble,
} from '@carboncopyco/ui';
import { MMMDoYYYY } from '@carboncopyco/utils';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useEffect, useState } from 'react';
import { useDebounce } from 'use-debounce';
import { useWorkspacesMap } from '../../../hooks/useWorkspacesMap';
import { TileImageSquare } from '../../GridTiles';
import SearchControls from '../../SearchControls';
import { creatorFilterOptions, sortOptions } from '../../consts';

type ImportChangesModalProps = {
  open: boolean;
  onClose: () => void;
  selectedDemo: string;
  setSelectedDemo: (id: string) => void;
  onConfirm: () => void;
  organizationId: string;
} & ({ showLoader: boolean; loaderLabel: string } | never);

export const ImportChangesModal = ({
  open,
  onClose,
  selectedDemo,
  setSelectedDemo,
  onConfirm,
  showLoader,
  loaderLabel,
  organizationId,
}: ImportChangesModalProps) => {
  const {
    items,
    error,
    isLoading,
    isLoadingBatch,
    fetchFirstBatch,
    fetchNextBatch,
  } = useInfiniteScroll<SearchResult>(useLazyGetSearchResultsQuery);
  const demos = items as IDemo[];
  const [sort, setSort] = useState<Sort>('alphabet');
  const [creatorFilter, setCreatorFilter] = useState<CreatorFilter>('all');
  const [inputValue, setInputValue] = useState<string>('');
  const [query] = useDebounce(inputValue, 500);
  const resetFilters = () => {
    setCreatorFilter('all');
  };
  const flags = useFlags();
  const isAllControlsDefault = creatorFilter === 'all';
  const workspacesMap = useWorkspacesMap();

  useEffect(() => {
    fetchFirstBatch({
      organizationId,
      entities: ['demo'],
      query,
      mineOnly: creatorFilter === 'mineOnly',
      sort,
    });
    setSelectedDemo('');
  }, [sort, creatorFilter, query]);

  const handleClose = () => {
    onClose();
  };

  return (
    <Modal
      onClose={handleClose}
      open={open}
      title="Import edits from existing Demo"
    >
      <div className="w-ds-lg-modal h-ds-582 flex flex-col mt-ds-2.5">
        {error ? (
          <div className="h-full w-full">
            <ErrorBox
              contained
              title="Error fetching demos"
            />
          </div>
        ) : showLoader ? (
          <div className="h-full w-full flex flex-col items-center justify-center">
            <div className="my-ds-10">
              <LoadingDots />
            </div>
            <TextBase
              color="text-ds-natural-600"
              label={loaderLabel}
            />
          </div>
        ) : (
          <>
            <TextBase label="Select a demo to import its variables, content rules, text replacements, image replacements etc." />
            <div className="flex justify-between mt-ds-8">
              <Flex
                className="w-full"
                justifyContent="space-between"
                alignItems="center"
              >
                <SearchControls
                  creatorFilterDropdown={{
                    value: creatorFilter,
                    onSelect: setCreatorFilter,
                    items: creatorFilterOptions,
                  }}
                  sortDropdown={{
                    value: sort,
                    onSelect: setSort,
                    items: sortOptions,
                  }}
                />
                <div className="relative w-80">
                  <InputText
                    value={inputValue}
                    placeholder="Search"
                    onChange={(event) => {
                      setInputValue(event.currentTarget.value);
                    }}
                  />
                </div>
              </Flex>
            </div>
            <Box className="flex-grow mt-ds-8">
              {isLoading ? (
                <SkeletonImageLoading cols={4} />
              ) : demos?.length ? (
                <ScrollContainer
                  border
                  onReachBottom={() => {
                    fetchNextBatch({
                      organizationId,
                      entities: ['demo'],
                      query,
                      mineOnly: creatorFilter === 'mineOnly',
                      sort,
                    });
                  }}
                >
                  <div className="pr-2.5">
                    <Grid<IDemo>
                      data={demos}
                      renderItem={(demo) => {
                        const workspace = workspacesMap[demo.workspaceId] ?? {
                          workspaceName: 'Untitled workspace',
                        };

                        return (
                          <TooltipBubble
                            title={`${demo.demoName}, ${workspace.workspaceName}`}
                            placement="top"
                            interactive={false}
                            followCursor
                            disabled={!flags.platformWorkspaces}
                          >
                            <TileImageSquare
                              selected={String(demo.id) === selectedDemo}
                              onClick={() => setSelectedDemo(String(demo.id))}
                              title={demo.demoName}
                              caption={
                                flags.platformWorkspaces
                                  ? workspace.workspaceName
                                  : ''
                              }
                              createTime={MMMDoYYYY(demo.createTime)}
                              image={demo.image}
                              icon={
                                <DemoIndicatorColoredIcon
                                  color={ColorHex.accent3100}
                                  size={30}
                                />
                              }
                            />
                          </TooltipBubble>
                        );
                      }}
                    />
                  </div>
                  <div className="flex justify-center min-ds-5 pt-ds-5">
                    {isLoadingBatch && <LoadingDots />}
                  </div>
                </ScrollContainer>
              ) : (
                <EmptyView
                  illustration={<SearchNoMatchIllustration />}
                  title={
                    <Flex
                      col
                      alignItems="center"
                    >
                      <div className="mb-ds-4">
                        <TextLargeParagraph
                          color="text-ds-natural-600"
                          label={`No matches found for "${query}"`}
                        />
                      </div>
                      <Button
                        secondary
                        label="Clear all filters"
                        onClick={resetFilters}
                        disabled={isAllControlsDefault}
                      />
                    </Flex>
                  }
                />
              )}
            </Box>
            <Flex
              className="mt-ds-10"
              justifyContent="space-between"
            >
              <MessageBox
                warn
                leftIcon={<InfoIcon />}
                label="Depending on your selection, some text and image edits might not apply"
              />
              <Button
                label="Create demo and import edits"
                disabled={!selectedDemo}
                onClick={onConfirm}
              />
            </Flex>
          </>
        )}
      </div>
    </Modal>
  );
};
